import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { apiFetch, unwrap, setTokens, clearTokens, getTokens, SESSION_EXPIRED_EVENT } from '@/lib/api';
import { mergeClientProfileIntoBusinessData } from '@/lib/clientProfileMerge';
import { normalizeApiRole } from '@/lib/routing';
import { clearAllTAOfflineSnapshots, clearTAOfflineSnapshots } from '@/lib/taOfflineStore';

const AppContext = createContext();

export function mapUserFromApi(raw) {
  if (!raw) return null;
  const ta = raw.technical_assistant;
  const aud = raw.assigned_auditor;
  return {
    id: raw.id,
    name: raw.name ?? '',
    email: raw.email ?? '',
    phone: raw.phone ?? '',
    nrp: raw.nrp ?? '',
    role: normalizeApiRole(raw.role),
    client_id: raw.client_id ?? null,
    ta_level: raw.ta_level ?? null,
    reports_to_user_id: raw.reports_to_user_id ?? null,
    organization: raw.organization ?? '',
    payment_status: raw.payment_status ?? null,
    active_packages: Array.isArray(raw.active_packages) ? raw.active_packages : [],
    checkout_payment:
      raw.checkout_payment && typeof raw.checkout_payment === 'object'
        ? {
            id: raw.checkout_payment.id ?? '',
            status: raw.checkout_payment.status ?? '',
            packages: Array.isArray(raw.checkout_payment.packages) ? raw.checkout_payment.packages : [],
          }
        : null,
    mfa_totp_enabled: Boolean(raw.mfa_totp_enabled),
    technical_assistant:
      ta && typeof ta === 'object'
        ? {
            id: ta.id ?? '',
            name: ta.name ?? '',
            email: ta.email ?? '',
            nrp: ta.nrp ?? '',
          }
        : null,
    assigned_auditor:
      aud && typeof aud === 'object'
        ? {
            id: aud.id ?? '',
            name: aud.name ?? '',
            email: aud.email ?? '',
            nrp: aud.nrp ?? '',
          }
        : null,
    isLoggedIn: true,
  };
}

const initialUserLoggedOut = {
  id: null,
  name: '',
  email: '',
  phone: '',
  nrp: '',
  role: null,
  client_id: null,
  ta_level: null,
  reports_to_user_id: null,
  organization: '',
  payment_status: null,
  active_packages: [],
  checkout_payment: null,
  mfa_totp_enabled: false,
  technical_assistant: null,
  assigned_auditor: null,
  isLoggedIn: false,
};

const initialApplication = {
  id: null,
  certificationType: null,
  currentPhase: 'pre-assessment',
  currentStep: 0,
  status: 'draft',
  businessData: {
    companyName: '',
    address: '',
    productType: '',
    picName: '',
    picPhone: '',
    picEmail: '',
    marketingArea: '',
    marketingSystem: '',
    nib: '',
    businessScale: '',
    distributionPermit: '',
  },
  documents: [],
};

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(initialUserLoggedOut);
  const userIdForStorageCleanupRef = useRef(/** @type {string|null} */ (null));
  if (user?.id != null) {
    userIdForStorageCleanupRef.current = String(user.id);
  }
  const [authHydrated, setAuthHydrated] = useState(false);
  const [application, setApplication] = useState(initialApplication);
  const [notifications, setNotifications] = useState([]);

  const refreshUser = useCallback(async () => {
    const json = await apiFetch('/me');
    const data = unwrap(json);
    let apiUser = {};
    let clientProfile = null;
    if (data && typeof data === 'object') {
      const { tenant_profile, ...rest } = data;
      apiUser = rest;
      clientProfile = tenant_profile ?? null;
    }
    setUser(mapUserFromApi(apiUser) ?? initialUserLoggedOut);
    if (clientProfile && typeof clientProfile === 'object') {
      setApplication((prev) => ({
        ...prev,
        businessData: mergeClientProfileIntoBusinessData(prev.businessData, clientProfile),
      }));
    }
    return data;
  }, []);

  useEffect(() => {
    const onSessionExpired = () => {
      const id = userIdForStorageCleanupRef.current;
      if (id) {
        clearTAOfflineSnapshots(id).catch(() => {});
      } else {
        clearAllTAOfflineSnapshots().catch(() => {});
      }
      setUser(initialUserLoggedOut);
      setApplication(initialApplication);
      setNotifications([]);
      userIdForStorageCleanupRef.current = null;
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { access } = getTokens();
      if (!access) {
        if (!cancelled) {
          setUser(initialUserLoggedOut);
          setAuthHydrated(true);
        }
        return;
      }
      try {
        await refreshUser();
      } catch {
        if (!cancelled) {
          clearTokens();
          const id = userIdForStorageCleanupRef.current;
          if (id) {
            clearTAOfflineSnapshots(id).catch(() => {});
          } else {
            clearAllTAOfflineSnapshots().catch(() => {});
          }
          userIdForStorageCleanupRef.current = null;
          setUser(initialUserLoggedOut);
        }
      } finally {
        if (!cancelled) setAuthHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  const loginWithCredentials = useCallback(async (identifier, password) => {
    const loginRes = await apiFetch('/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier, password }),
      skipAuth: true,
    });
    const loginData = unwrap(loginRes);
    if (loginData?.mfa_required && loginData?.mfa_token) {
      return {
        mfaRequired: true,
        mfaToken: loginData.mfa_token,
        expiresIn: loginData.expires_in ?? null,
      };
    }
    if (loginData?.access_token && loginData?.refresh_token) {
      setTokens(loginData.access_token, loginData.refresh_token);
    }
    await refreshUser();
    return { mfaRequired: false };
  }, [refreshUser]);

  const completeMFALogin = useCallback(async (mfaToken, code) => {
    const res = await apiFetch('/auth/mfa/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mfa_token: mfaToken, code: String(code).replace(/\s/g, '') }),
      skipAuth: true,
    });
    const data = unwrap(res);
    if (data?.access_token && data?.refresh_token) {
      setTokens(data.access_token, data.refresh_token);
    }
    await refreshUser();
  }, [refreshUser]);

  const registerAccount = useCallback(async (payload) => {
    await apiFetch('/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      skipAuth: true,
    });
  }, []);

  const bootstrapMeClient = useCallback(async ({ name, code, address }) => {
    const json = await apiFetch('/me/client', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, code: code || undefined, address: address || '' }),
    });
    const data = unwrap(json);
    await refreshUser();
    return data;
  }, [refreshUser]);

  const logout = useCallback(async () => {
    const { refresh } = getTokens();
    const id = userIdForStorageCleanupRef.current;
    try {
      if (refresh) {
        await apiFetch('/auth/logout', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refresh }),
          skipAuth: true,
        });
      }
    } catch {
      /* still clear locally */
    }
    clearTokens();
    if (id) {
      try {
        await clearTAOfflineSnapshots(id);
      } catch {
        /* ignore */
      }
    } else {
      try {
        await clearAllTAOfflineSnapshots();
      } catch {
        /* ignore */
      }
    }
    userIdForStorageCleanupRef.current = null;
    setUser(initialUserLoggedOut);
    setApplication(initialApplication);
    setNotifications([]);
  }, []);

  const setUserFromSession = useCallback((apiUser) => {
    setUser(mapUserFromApi(apiUser) ?? initialUserLoggedOut);
  }, []);

  const updateApplication = useCallback((updates) => {
    setApplication((prev) => ({ ...prev, ...updates }));
  }, []);

  const selectCertificationType = useCallback((type) => {
    setApplication((prev) => ({
      ...prev,
      certificationType: type,
      id: `APP-${Date.now()}`,
    }));
  }, []);

  const updateBusinessData = useCallback((data) => {
    setApplication((prev) => ({
      ...prev,
      businessData: { ...prev.businessData, ...data },
    }));
  }, []);

  const uploadDocument = useCallback((document) => {
    setApplication((prev) => ({
      ...prev,
      documents: [
        ...prev.documents,
        { ...document, id: `DOC-${Date.now()}`, uploadedAt: new Date().toISOString() },
      ],
    }));
  }, []);

  const submitApplication = useCallback(() => {
    setApplication((prev) => ({
      ...prev,
      status: 'submitted',
      currentStep: 2,
    }));
  }, []);

  const markNotificationRead = useCallback((notifId) => {
    if (notifId == null) return;
    setNotifications((prev) =>
      prev.map((notif) => (notif.id === notifId ? { ...notif, read: true } : notif)),
    );
  }, []);

  const addNotification = useCallback((notification) => {
    const id =
      notification.id != null && String(notification.id).trim() !== ''
        ? String(notification.id)
        : `NOTIF-${Date.now()}`;
    const ts =
      notification.timestamp != null && String(notification.timestamp).trim() !== ''
        ? notification.timestamp
        : new Date().toISOString();
    setNotifications((prev) => {
      const exists = prev.some((n) => n.id === id);
      if (exists) {
        return prev.map((n) =>
          n.id === id ? { ...n, ...notification, id, timestamp: n.timestamp ?? ts, read: n.read } : n,
        );
      }
      return [{ ...notification, id, timestamp: ts, read: notification.read ?? false }, ...prev];
    });
  }, []);

  /** Replace client activity-derived notifications (id prefix `activity-`); preserves read state and keeps other notifications. */
  const syncActivityNotifications = useCallback((items) => {
    setNotifications((prev) => {
      const prevById = new Map(prev.map((n) => [n.id, n]));
      const nonActivity = prev.filter((n) => !String(n.id).startsWith('activity-'));
      const merged = (items || []).map((item) => ({
        source: 'client_activity',
        type: item.type ?? 'info',
        ...item,
        read: prevById.get(item.id)?.read ?? item.read ?? false,
      }));
      return [...merged, ...nonActivity];
    });
  }, []);

  const value = {
    user,
    authHydrated,
    application,
    notifications,
    loginWithCredentials,
    completeMFALogin,
    registerAccount,
    bootstrapMeClient,
    refreshUser,
    setUserFromSession,
    logout,
    updateApplication,
    selectCertificationType,
    updateBusinessData,
    uploadDocument,
    submitApplication,
    markNotificationRead,
    addNotification,
    syncActivityNotifications,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
